import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
  Box, Container, Paper, Typography, TextField, Button,
  Snackbar, Alert, CircularProgress,
} from '@mui/material';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import { useTranslation } from 'react-i18next';

import { NAV_BG, AMBER } from '../theme';

const DriverDetailsPage = () => {
  const { authTokens, backendUrl, pageTitle } = useContext(AuthContext);
  const navigate = useNavigate();
  const { profile_id } = useParams();
  const isEdit = Boolean(profile_id);
  const { t } = useTranslation();

  document.title = pageTitle + ' - ' + (isEdit ? t('drivers.edit_title') : t('drivers.add_title'));

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone_number: '' });
  const [saving, setSaving] = useState(false);
  const [loadingDriver, setLoadingDriver] = useState(isEdit);
  const [error, setError] = useState('');

  const loadDriver = useCallback(async () => {
    const response = await fetch(backendUrl + 'api/drivers/', {
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) },
    });
    if (!response.ok) { setError(t('drivers.failed_to_load')); setLoadingDriver(false); return; }
    const data = await response.json();
    const driver = data.find((d) => d.profile_id === profile_id);
    if (!driver) { setError(t('drivers.not_found')); setLoadingDriver(false); return; }
    setForm({ first_name: driver.firstname || '', last_name: driver.lastname || '', email: driver.email || '', phone_number: driver.phone_number || '' });
    setLoadingDriver(false);
  }, [profile_id]);

  useEffect(() => { if (isEdit) { loadDriver(); } }, [isEdit, loadDriver]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone_number') {
      setForm((prev) => ({ ...prev, phone_number: value.replace(/[^0-9+\- ]/g, '') }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSave = async () => {
    if (!form.first_name.trim()) { setError(t('common.first_name_required')); return; }
    if (!form.last_name.trim()) { setError(t('common.last_name_required')); return; }
    if (!form.email) { setError(t('common.email_required')); return; }
    if (!isValidEmail(form.email)) { setError(t('common.email_invalid')); return; }
    if (!form.phone_number.trim()) { setError(t('common.phone_number_required')); return; }
    if (!/^\d{1,14}$/.test(form.phone_number)) { setError(t('common.phone_number_invalid')); return; }
    setSaving(true);
    setError('');
    const payload = isEdit ? { id: profile_id, ...form } : { ...form };
    const response = await fetch(backendUrl + 'api/save-driver/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) },
      body: JSON.stringify(payload),
    });
    setSaving(false);
    if (response.ok) { navigate('/drivers'); }
    else { const text = await response.text(); setError(text || t('drivers.failed_to_save')); }
  };

  if (loadingDriver) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ mt: 12, mb: 6 }}>
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', p: 4, boxShadow: '0 4px 24px rgba(15,45,94,0.07)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <DirectionsBusIcon sx={{ color: NAV_BG, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
            {isEdit ? t('drivers.edit_title') : t('drivers.add_title')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label={t('common.first_name')} name="first_name" value={form.first_name} onChange={handleChange} fullWidth size="small" required sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER } }} />
            <TextField label={t('common.last_name')} name="last_name" value={form.last_name} onChange={handleChange} fullWidth size="small" required sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label={t('common.email')} name="email" type="email" value={form.email} onChange={handleChange} fullWidth size="small" required sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER } }} />
            <TextField label={t('common.phone_number')} name="phone_number" value={form.phone_number} onChange={handleChange} fullWidth size="small" required sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER } }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ background: NAV_BG, fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#163571' } }}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/drivers')}
            sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: AMBER, color: NAV_BG } }}>
            {t('common.cancel')}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={!!error} autoHideDuration={20000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Container>
  );
};

export default DriverDetailsPage;
